import { DakenService, LogService, ModalAcceptanceContract, SheHulkService, TioPatinhasService, ToastHelper, UserHelper } from '@hub/shared'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  openModal: boolean
  transactionId?: string
  closeModal?: () => any
}

export default ({ openModal, transactionId, closeModal }: Props) => {
  const toast = useMemo(() => new ToastHelper().toast, [])
  const logService = useMemo(() => new LogService(), [])
  const sheHulkService = useMemo(() => new SheHulkService(), [])
  const tioService = useMemo(() => new TioPatinhasService(), [])
  const dakenService = useMemo(() => new DakenService(), [])

  const [t] = useTranslation('modalAcceptAntecipation')

  const [loading, setLoading] = useState(true)
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [contract, setContract] = useState('')
  const userHelper = useMemo(() => new UserHelper(), [])

  logService.init('Modal Accept Antecipation')

  const handleCloseModal = () => {
    if (closeModal && typeof closeModal === 'function') {
      closeModal()
    }
  }

  useEffect(() => {
    if (openModal && transactionId) {
      setLoading(true)
      sheHulkService
        .getAntecipationContract(transactionId)
        .then((e) => {
          if (e?.status >= 400) {
            throw e
          }
          setContract(e?.contract)
        })
        .catch((error) => {
          logService.error({ message: error?.data?.message, error })
          if (closeModal && typeof closeModal === 'function') {
            closeModal()
          }
          toast.error(t('defaultError'), {
            closeButton: true,
            className: 'full text-bold',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            duration: 10000,
            notOverClick: true,
            immediately: true,
          })
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [openModal, transactionId, sheHulkService, logService, closeModal, toast, t])

  const acceptContract = useCallback(() => {
    setLoadingSubmit(true)
    tioService
      .postAcceptedAntecipationContract(transactionId || '')
      .then((e) => {
        if (e?.transaction?.anticipationContractAccepted) {
          if (closeModal && typeof closeModal === 'function') {
            dakenService
              ?.getInfluencersInfo()
              .then((user) => userHelper.setUser(user))
              .catch((error) => {
                logService.error({ message: 'Failed to get influencers info', error })
                toast.error(t('influencersInfoError'), {
                  closeButton: true,
                  className: 'full text-bold',
                  position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
                  duration: 3600000,
                  notOverClick: true,
                  immediately: true,
                })
              })
              .finally(() => closeModal())
          }
          toast.success(t('success'), {
            closeButton: true,
            className: 'full text-bold',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            duration: 10000,
            notOverClick: true,
            immediately: true,
          })
        }
      })
      .catch((error) => {
        logService.error({ message: error?.data?.message, error })
        toast.error(t('defaultError'), {
          closeButton: true,
          className: 'full text-bold',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
      })
      .finally(() => {
        setLoadingSubmit(false)
      })
  }, [transactionId, tioService, logService, closeModal, toast, t, dakenService, userHelper])

  return (
    <ModalAcceptanceContract
      modalTitle={t('title')}
      openModal={openModal}
      contract={contract}
      acceptTerms={[t('readTerms'), t('cantUndone')]}
      loading={loading}
      loadingSubmit={loadingSubmit}
      contractAccepted={() => acceptContract()}
      contractRefused={() => handleCloseModal()}
      closeModal={() => handleCloseModal()}
    />
  )
}
